import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Layers, Camera, Mic, Droplet, FlaskConical, Gauge, Hammer, FolderCog, Pencil, FileUp } from 'lucide';
import { api } from '../api.js';
import { lucideIcon } from '../icon.js';
import { formatDateTimeSlash, formatTime, parseTimestamp } from '../utils/datetime.js';
import { codeLabel } from '../utils/codelabels.js';

/**
 * Icoon per soort gebeurtenis, zodat je op type kunt scannen zonder te lezen. Dezelfde
 * iconen als elders in de applicatie voor dezelfde begrippen — een ander icoon voor
 * hetzelfde ding kost meer dan het oplevert.
 */
const ENTITY_ICON = {
  boring: Hammer, layer: Layers, monster: FlaskConical, peilbuis: Gauge,
  watermonster: Droplet, afwerking: Hammer, photo: Camera, recording: Mic,
  lagen: Layers, project: FolderCog,
  // Zelfde erlenmeyer als bij een grondmonster: beide gaan over laboratoriumwerk, en een
  // apart icoon voor dat onderscheid zegt minder dan het kost.
  labopdracht: FlaskConical,
  // Hetzelfde icoon als het sleepvlak waarmee je het bestand erin zet, zodat 'importeren'
  // er overal hetzelfde uitziet.
  import: FileUp,
};

/** Filter op soort. De sleutel is de entiteit in het log; 'lagen' is de AI-verwerking. */
const KIND_FILTERS = [
  { value: '', label: 'Alle soorten' },
  { value: 'recording', label: "Opnames" },
  { value: 'photo', label: "Foto's" },
  { value: 'boring', label: 'Boringen' },
  { value: 'layer', label: 'Lagen' },
  { value: 'monster', label: 'Grondmonsters' },
  { value: 'peilbuis', label: 'Peilbuizen' },
  { value: 'watermonster', label: 'Watermonsters' },
  { value: 'labopdracht', label: 'Labopdrachten' },
  { value: 'import', label: 'Imports' },
];

/**
 * Entiteiten waarvan het ONDERWERP vrije tekst is. Die mogen samengevoegd worden ook als het
 * onderwerp per regel verschilt: vier bewerkingen aan de omschrijving van een foto leveren
 * anders vier regels op die alle vier iets anders heten, terwijl het één handeling was.
 */
const FREE_TEXT_SUBJECT = new Set(['photo']);

/** Ziet dit eruit als een id in plaats van een naam die iemand heeft ingetypt? */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Aantal SAMENGEVOEGDE regels op de tegel. Genoeg om te zien wat er vandaag is gebeurd, klein
 * genoeg om de rest van de projectpagina niet weg te duwen. Bewust geen hoogtegrens: die kapt
 * een regel halverwege af, en met dagkoppen erbij is niet elke regel meer even hoog.
 */
const ROWS_ON_TILE = 8;

/** Hoeveel RUWE logregels we ophalen; na samenvoegen blijft daar ruwweg een derde van over. */
const FETCH_TILE = 80;
const FETCH_ALL = 500;

/**
 * "Laatste activiteit"-tegel op het projectoverzicht.
 *
 * Toont wie wat wanneer wijzigde, met de boring als klikbare link. De backend levert
 * gestructureerde gebeurtenissen (entiteit + actie + aantal); de zin wordt hier gemaakt,
 * zodat we de formulering kunnen bijschaven zonder het log te hoeven herschrijven.
 */
export class ActivityTile extends LitElement {
  static properties = {
    projectId: { type: String },
    // Alternatieve bron: alle activiteit van één PERSOON over alle projecten van het bedrijf
    // (popup op de Team-pagina). Dezelfde zinsopbouw, gekanteld — niet 'wat gebeurde er in
    // dit project' maar 'wat deed deze persoon'. Elke regel krijgt dan zijn project erbij.
    memberId: { type: String },
    refreshSignal: { type: Number },
    soilStandard: { type: String },   // bepaalt welke codelijst een code vertaalt
    _items: { state: true },
    _lastRecording: { state: true },
    _loading: { state: true },
    _open: { state: true },   // uitgeklapte groepen (index)
    _allOpen: { state: true }, // popup met de volledige geschiedenis
    _kind: { state: true },   // filter op soort gebeurtenis ('' = alles)
  };

  constructor() {
    super();
    this._items = [];
    this._lastRecording = null;
    this._loading = true;
    this._open = {};
    this._allOpen = false;
    this._kind = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
    this._onEsc = (e) => { if (e.key === 'Escape' && this._allOpen) this._allOpen = false; };
    document.addEventListener('keydown', this._onEsc);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onEsc);
    super.disconnectedCallback();
  }

  updated(changed) {
    if (changed.has('projectId') && changed.get('projectId') !== undefined) this._load();
    if (changed.has('memberId') && changed.get('memberId') !== undefined) this._load();
    // Ververst zodra het project een wijziging meldt (WS), zodat je niet van tab hoeft te wisselen.
    if (changed.has('refreshSignal') && changed.get('refreshSignal') !== undefined) this._load();
    // Het filter gaat naar de server, dus een andere soort betekent opnieuw ophalen. Per
    // persoon is alles al binnen en filtert _grouped lokaal.
    if (changed.has('_kind') && changed.get('_kind') !== undefined && !this.memberId) this._load();
  }

  async _load() {
    if (!this.projectId && !this.memberId) return;
    this._loading = true;
    try {
      // Ruim ophalen: autosave levert veel losse regels die we hieronder samenvoegen.
      const res = this.memberId
        ? await api.getMemberActivity(this.memberId, FETCH_ALL)
        : await api.getProjectActivity(this.projectId, this._loadedAll ? FETCH_ALL : FETCH_TILE, this._kind);
      this._items = res?.activity || [];
      this._lastRecording = res?.lastFieldRecording || null;
    } catch (_) {
      this._items = [];
    } finally {
      this._loading = false;
    }
  }

  /**
   * Alleen de tijd. De datum staat in de dagkop erboven, en die stond hiervoor op elke regel
   * opnieuw: acht keer "15/08/2026" onder elkaar vertelt bij de tweede regel al niets meer,
   * maar kost wel een derde van de regelbreedte.
   *
   * Via de gedeelde helper, zodat deze tijd in dezelfde zone staat als die in de boringenlijst.
   * Hier stond Europe/Amsterdam apart hardgecodeerd; toen de rest van de applicatie de tijdzone
   * van de kijker ging volgen, zou deze regel als enige in Nederland zijn blijven staan.
   */
  _time(ts) {
    return formatTime(ts) === '—' ? '' : formatTime(ts);
  }

  /**
   * Dagkop: bij recente dagen woorden, daarvoor de datum. "Vandaag" en "Vrijdag 7 augustus"
   * lees je sneller dan een datum die je eerst moet omrekenen naar hoe lang geleden dat was.
   * Het jaar staat er alleen bij als het niet dit jaar is -- anders is het ruis.
   */
  _dayLabel(d) {
    // getFullYear/getMonth/getDate lezen de datum in de tijdzone van de browser. Dat is nu ook
    // de zone waarin de tijden eronder staan, dus 'Vandaag' en de regels eronder horen weer bij
    // elkaar; daarvoor kon de kop 'Vandaag' zeggen boven regels van gisteravond.
    const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const now = new Date();
    const diff = Math.round((day(now) - day(d)) / 86400000);
    const date = d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });
    if (diff === 0) return `Vandaag · ${date}`;
    if (diff === 1) return `Gisteren · ${date}`;
    const weekday = d.toLocaleDateString('nl-NL', { weekday: 'long' });
    const year = d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : '';
    return `${weekday} ${date}${year}`;
  }

  /**
   * Voeg opeenvolgende gebeurtenissen van dezelfde soort samen: zelfde persoon, zelfde
   * boring, zelfde entiteit én actie, binnen een half uur. Autosave levert anders tien
   * losse regels voor één bewerksessie aan B12.
   *
   * Twee regels die het uit elkaar houden:
   * - Bij een vrij-tekstonderwerp (een foto heet naar zijn omschrijving) telt het onderwerp
   *   NIET mee. Wie drie keer aan dezelfde omschrijving sleutelt, verandert bij elke opslag
   *   de naam van de regel — en kreeg zo drie regels voor één handeling.
   * - Nooit over een dagovergang heen. Het halfuursvenster zou 23:55 en 00:05 samenvoegen,
   *   en dan staat de regel onder de verkeerde dagkop.
   */
  _grouped() {
    const WINDOW_MS = 30 * 60 * 1000;
    const out = [];
    // Per persoon staat alles al in het geheugen; het soortfilter werkt dan hier in plaats
    // van op de server.
    const items = this.memberId && this._kind ? this._items.filter((a) => a.entity === this._kind) : this._items;
    for (const a of items) {
      const t = parseTimestamp(a.createdAt)?.getTime() ?? 0;
      const prev = out[out.length - 1];
      const sameKind = prev
        && prev.actorName === a.actorName && prev.entity === a.entity
        && prev.action === a.action && prev.boringId === a.boringId
        && (prev.subject === a.subject || FREE_TEXT_SUBJECT.has(a.entity));
      const close = prev && Math.abs((prev._oldest ?? 0) - t) <= WINDOW_MS
        && prev._day === this._dayKey(a.createdAt);
      if (sameKind && close) {
        prev.events += 1;
        prev.count += (a.count || 1);
        this._mergeFields(prev.fields, this._fieldsOf(a));
        prev._oldest = t;
      } else {
        out.push({ ...a, events: 1, count: a.count || 1, fields: this._fieldsOf(a), _oldest: t, _day: this._dayKey(a.createdAt), _key: out.length });
      }
    }
    return out;
  }

  /** Kalenderdag als sorteerbare sleutel; puur intern, nooit op het scherm. */
  _dayKey(ts) {
    const d = parseTimestamp(ts);
    return d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '';
  }

  /** Groepen opdelen in dagen, in dezelfde volgorde als ze binnenkwamen (nieuwste eerst). */
  _byDay(groups) {
    const days = [];
    for (const g of groups) {
      const key = g._day;
      if (!days.length || days[days.length - 1].key !== key) {
        const d = parseTimestamp(g.createdAt);
        days.push({ key, label: d ? this._dayLabel(d) : '', rows: [] });
      }
      days[days.length - 1].rows.push(g);
    }
    return days;
  }

  /**
   * De grijze tweede regel: waar ging het over, zonder de zin erboven te kapen.
   *
   * Een fotobeschrijving kan een alinea zijn en een bewerksessie raakt zes velden — beide
   * horen niet middenin een zin. Hier staan ze op één regel die met CSS wordt afgekapt, dus
   * ze nemen nooit meer dan hun eigen regel in beslag.
   */
  _summary(a, scope) {
    if (a.entity === 'photo') {
      const sub = a.subject || null;
      return sub && !/\.(jpe?g|png|webp|heic|heif)$/i.test(sub) ? sub : null;
    }
    const fields = a.fields || [];
    if (fields.length > 1) return fields.map((f) => this._fieldLabel(f.field, scope)).join(', ');
    return null;
  }

  _fieldsOf(a) {
    try {
      const d = JSON.parse(a.detail || '[]');
      if (!Array.isArray(d)) return [];
      // Oud formaat was een lijst veldnamen; nieuw formaat { field, from, to }.
      return d.map((x) => (typeof x === 'string' ? { field: x, from: null, to: null } : x));
    } catch (_) { return []; }
  }

  /**
   * Voeg de velden van een oudere gebeurtenis samen met die van de groep. Items komen
   * nieuwste-eerst binnen, dus de bestaande 'to' is de nieuwste waarde en de 'from' van
   * de oudere gebeurtenis is het echte beginpunt — zo lees je de hele sessie als één stap.
   */
  _mergeFields(target, incoming) {
    for (const f of incoming) {
      const hit = target.find((t) => t.field === f.field);
      if (hit) hit.from = f.from;
      else target.push({ ...f });
    }
  }

  /**
   * Waarde leesbaar maken: leeg expliciet, booleans in het Nederlands en codes als term.
   * We tonen hier ruwe opgeslagen waarden zonder formulier eromheen, dus zonder vertaling
   * lees je 'S' en 'gr' in plaats van 'silt' en 'grijs'.
   */
  _val(v, field = null) {
    if (v == null || v === '') return '—';
    const s = String(v);
    // Alleen echte booleans vertalen — '1'/'0' zijn vaak codelijstwaarden.
    if (s === 'true') return 'Ja';
    if (s === 'false') return 'Nee';
    // Blobvelden komen binnen als 'wm:<monster>:<veld>'; alleen de veldnaam telt.
    const bare = field ? String(field).split(':').pop() : null;
    return (bare && codeLabel(bare, s, this.soilStandard)) || s;
  }

  /**
   * Waar gáát een groep wijzigingen eigenlijk over?
   *
   * Watermonsters en afwerking hangen als JSON-blob aan de boring, dus komen ze binnen als
   * een boring-update. "wijzigde B01-PB01" is dan misleidend — je ziet niet dat het om een
   * watermonster ging. Zijn álle velden van hetzelfde soort, dan benoemen we dat in de zin.
   */
  _scope(a) {
    const fields = a.fields || [];
    if (!fields.length) return null;
    const kinds = new Set(fields.map((f) => String(f.field || '').split(':')[0]));
    if (kinds.size !== 1) return null;                    // gemengd → geen aanname doen
    const kind = [...kinds][0];
    if (kind === 'afw') return { kind: 'afw' };
    if (kind !== 'wm') return null;
    // Alleen de VELDwijzigingen tellen mee voor de vraag "over welk watermonster gaat dit?".
    // Een regel als `wm:1234` (toegevoegd/verwijderd) noemt het monster ook, maar bij het
    // toevoegen heeft het er nog geen code en heet het anders dan bij de invulling die er in
    // dezelfde bewerksessie op volgt. Meetellen betekende twee namen, en dan kreeg élke regel
    // het monster als voorvoegsel: "1234 · Bijkleur, 1234 · Kleursterkte, 1234 · ..." negen
    // keer achter elkaar.
    const metVeld = fields.filter((f) => String(f.field).split(':').length >= 3);
    const namen = new Set((metVeld.length ? metVeld : fields).map((f) => String(f.field).split(':')[1]));
    return { kind: 'wm', name: namen.size === 1 ? [...namen][0] : null };
  }

  /**
   * Veldnaam → wat de gebruiker in het formulier ziet.
   *
   * De backend splitst de JSON-blobs uit naar `wm:<monster>:<veld>` en `afw:<blok>`. Staat
   * dat monster al in de zin erboven, dan herhalen we het niet op elke regel.
   */
  _fieldLabel(f, scope = null) {
    if (typeof f === 'string' && f.startsWith('wm:')) {
      const [, monster, key] = f.split(':');
      // Een id in plaats van een code komt uit logregels van vóór de fix in BoringService.
      // Die kunnen we niet meer repareren, maar wél verzwijgen: 'Watermonster' zonder naam
      // zegt meer dan 'Watermonster d89aaf26-3562-4d67-b5be-73c393d5c2b5'.
      const naam = UUID_RE.test(monster) ? null : monster;
      if (!key) return naam ? `Watermonster ${naam}` : 'Watermonster';
      return scope?.name || !naam ? this._fieldLabel(key) : `${naam} · ${this._fieldLabel(key)}`;
    }
    if (typeof f === 'string' && f.startsWith('afw:')) {
      const block = f.slice(4);
      const cap = block.charAt(0).toUpperCase() + block.slice(1);
      return scope?.kind === 'afw' ? cap : `Afwerking · ${block}`;
    }
    return {
      upperDepth: 'Diepte van', lowerDepth: 'Diepte tot', soilType: 'Grondsoort', mediaan: 'Mediaan',
      kleurMain: 'Hoofdkleur', kleurSub: 'Bijkleur', kleurShade: 'Tint',
      toevoeging1: 'Bijmenging 1', gradatie1: 'Gradatie 1', toevoeging2: 'Bijmenging 2', gradatie2: 'Gradatie 2',
      toevoeging3: 'Bijmenging 3', gradatie3: 'Gradatie 3', bzb: 'Bijzondere bestanddelen',
      oilWaterReaction: 'Olie/water-reactie', oilWaterObservation: 'Olie/water-waarneming',
      geurType: 'Geurtype', geurIntensity: 'Geursterkte', drillingSystem: 'Boorsysteem',
      mineraalarm: 'Mineraalarm', consistentie: 'Consistentie', opmerking: 'Opmerking',
      name: 'Naam', type: 'Type', boormeester: 'Boormeester', gwsCmMv: 'Grondwaterstand',
      gwsFound: 'Grondwater aangetroffen', referencePlane: 'Referentievlak', groundLevelType: 'Maaiveldtype',
      stopReason: 'Reden gestopt', x: 'X-coördinaat', y: 'Y-coördinaat', z: 'Maaiveldhoogte',
      afwerking: 'Afwerking', watermonsters: 'Watermonsters', datum: 'Datum',
      reviewedAt: 'Profiel goedgekeurd in het veld',
      // Peilbuis
      filterTopCm: 'Filter bovenkant', filterBottomCm: 'Filter onderkant', diameter: 'Buitendiameter',
      innerDiameterMm: 'Binnendiameter', material: 'Materiaal', flowType: 'Doorloop',
      filterSieveUsed: 'Filterkous', unwatered: 'Leeggezogen', rinsed: 'Gespoeld',
      pumpedL: 'Afgepompt (l)', waterUsageL: 'Werkwater (l)', ecWorkWater: 'EGV werkwater',
      installedAt: 'Geplaatst op', remark: 'Opmerking', referencePointType: 'Referentiepunt',
      // Watermonster — veldmeting
      ec: 'EGV', ph: 'pH', temperature: 'Temperatuur', oxygen: 'Zuurstof', redox: 'Redox',
      turbidityPrepump: 'Troebelheid voorpompen', turbiditySample: 'Troebelheid monster',
      // Watermonster — monstername
      monsternemer: 'Monsternemer', matrix: 'Matrix', sampledAt: 'Bemonsterd op',
      gwsBeforePrepumpCm: 'GWS voor voorpompen', gwsAfterPrepumpCm: 'GWS na voorpompen',
      gwsBeforeSamplingCm: 'GWS voor bemonstering', gwsReferencePoint: 'Referentievlak GWS',
      prepumpVolumeL: 'Voorgepompt volume', recommendedPrepumpVolumeL: 'Te voorpompen volume',
      flushVolumeL: 'Spoelvolume', yield: 'Opbrengst', pumpType: 'Pomptype',
      flowRate: 'Debiet', aerated: 'Belucht', transportDate: 'Datum vervoer',
      // Watermonster — zintuiglijk
      colourMain: 'Hoofdkleur', colourSub: 'Bijkleur', colourShade: 'Kleursterkte',
      clarity: 'Transparantie', specialConstituents: 'Bijzondere bestanddelen',
      floatingLayer: 'Drijflaag', floatingLayerThicknessMm: 'Dikte drijflaag',
      sinkingLayer: 'Zaklaag', sinkingLayerThicknessMm: 'Dikte zaklaag',
      bottles: 'Aantal flessen',
      // Foto
      description: 'Omschrijving', targetType: 'Hoort bij', targetLabel: 'Onderdeel',
      // Labopdracht — stonden hier niet in, dus las je 'urgencyCode, agreementCode' in beeld.
      reference: 'Orderreferentie', sampleType: 'Monstertype',
      certificateFormat: 'Certificaatcodering', urgencyCode: 'Urgentie',
      agreementCode: 'Afspraak', orderDate: 'Datum labopdracht',
      deliveryDate: 'Datum eerste levering', sampleDeliveryDate: 'Datum monsters',
      projectLeaderName: 'Projectleider', projectLeaderEmail: 'E-mail projectleider',
      remarks: 'Opmerking', status: 'Status', labOrderId: 'Ordernummer bij het lab',
      mengmonsters: 'Mengmonsters', potjes: 'Potjes', analyses: 'Analyses',
    }[f] || f;
  }

  /**
   * De boring-subtab waar dit onderdeel te vinden is. Klikken op het onderdeel moet je
   * brengen waar je het kunt bekijken, niet alleen bij de boring.
   */
  _tabFor(a, scope) {
    if (scope?.kind === 'wm') return 'watermonster';
    if (scope?.kind === 'afw') return 'afwerking';
    return { peilbuis: 'peilbuizen', watermonster: 'watermonster', afwerking: 'afwerking',
             monster: 'potjes', layer: 'lagen', photo: 'fotos' }[a.entity] || null;
  }

  /**
   * De gebeurtenis als zin, opgesplitst zodat het ONDERDEEL apart klikbaar is:
   * "wijzigde peilbuis [PB01] van [B99-PB01]". Zonder die opsplitsing zag je alleen de
   * boring en moest je zelf de juiste subtab zoeken.
   *
   * @returns {{lead: string, subject: string|null, tail: string}}
   */
  _phraseParts(a, scope = null) {
    const n = scope ? a.fields.length : a.count;

    // Watermonster en afwerking hangen als blob aan de boring → entiteit is 'boring'.
    if (scope?.kind === 'wm') {
      const lead = n > 1 ? `wijzigde ${n} velden van watermonster` : 'wijzigde watermonster';
      return scope.name ? { lead, subject: scope.name, tail: 'van' }
                        : { lead: 'wijzigde de watermonsters van', subject: null, tail: '' };
    }
    if (scope?.kind === 'afw') {
      return { lead: n > 1 ? `wijzigde ${n} velden van de` : 'wijzigde de', subject: 'afwerking', tail: 'van' };
    }

    const sub = a.subject || null;

    // Een foto benoemen we met zijn omschrijving. Een bestandsnaam als 'IMG_2481.jpg' is geen
    // omschrijving -- die zegt niets over wat er te zien is en maakt de regel alleen langer --
    // dus dan blijft het bij 'een foto'. Hier staat wél het woord 'boring' in de zin: een foto
    // hangt aan de boring als geheel, niet aan een onderdeel ervan.
    if (a.entity === 'photo') {
      const named = sub && !/\.(jpe?g|png|webp|heic|heif)$/i.test(sub) ? sub : null;
      const bor = a.boringName ? 'boring' : 'de boring';
      if (a.action === 'create') {
        if (named) return { lead: 'voegde foto', subject: named, tail: `toe aan ${bor}` };
        const many = a.count > 1 ? `${a.count} foto's` : 'een foto';
        return { lead: `voegde ${many} toe aan ${bor}`, subject: null, tail: '' };
      }
      // Bij een bewerking benoemen we WAT er veranderde, niet welke foto. De omschrijving is
      // vrije tekst en kan een alinea zijn; die als onderwerp in de zin zetten maakte de regel
      // onleesbaar -- en juist bij een gewijzigde omschrijving zegt de nieuwe waarde in de zin
      // niets, want je wilt zien wat er vóór stond. Dat staat nu in het uitklapbare verschil.
      if (a.action === 'update') {
        // Meerdere bewerkingen achter elkaar zijn samengevoegd tot deze regel. We tellen
        // GEBEURTENISSEN, niet foto's: het log legt niet vast wélke foto het was, dus
        // "wijzigde 4 foto's" zou een leugen zijn als iemand vier keer dezelfde omschrijving
        // bijschaafde -- precies wat er in de praktijk gebeurt.
        if (a.events > 1) return { lead: `wijzigde ${a.events} keer een foto bij ${bor}`, subject: null, tail: '' };
        const changed = new Set((a.fields || []).map((x) => x.field));
        const onlyDescription = changed.size === 1 && changed.has('description');
        const onlyTarget = changed.size > 0 && !changed.has('description');
        const what = onlyDescription ? 'de omschrijving van een foto'
                   : onlyTarget ? 'de koppeling van een foto'
                   : 'een foto';
        return { lead: `wijzigde ${what} in ${bor}`, subject: null, tail: '' };
      }
      if (a.action === 'delete') {
        return named ? { lead: 'verwijderde foto', subject: named, tail: `van ${bor}` }
                     : { lead: `verwijderde een foto van ${bor}`, subject: null, tail: '' };
      }
    }

    // Een labopdracht hangt aan het PROJECT, niet aan een boring. Elke zin hieronder eindigt
    // op 'van' of 'aan' omdat daar een boringnaam achteraan komt; bij een labopdracht blijft
    // die staart leeg en stopt de zin halverwege. Vandaar een eigen geval, met het
    // ordernummer als klikbaar onderwerp en zonder staart.
    if (a.entity === 'labopdracht') {
      const verb = { create: 'maakte labopdracht', update: 'wijzigde labopdracht',
                     delete: 'verwijderde labopdracht',
                     send: 'verstuurde labopdracht' }[a.action] || 'wijzigde labopdracht';
      if (!sub) return { lead: `${verb.replace(' labopdracht', '')} een labopdracht`, subject: null, tail: '' };
      if (a.action === 'update' && a.count > 1) {
        return { lead: `wijzigde ${a.count} velden van labopdracht`, subject: sub, tail: '' };
      }
      // Versturen eindigt op waar hij heen ging: dat is de helft van het nieuws.
      if (a.action === 'send') return { lead: verb, subject: sub, tail: 'naar het lab' };
      return { lead: verb, subject: sub, tail: a.action === 'create' ? 'aan' : '' };
    }

    // Een import hoort bij het PROJECT, net als een labopdracht: geen staart, want er komt geen
    // boringnaam achteraan. Het aantal boringen staat in de zin zelf — dat is wat je wilt weten
    // als je terugkijkt hoe dit project is ontstaan. De rest van de aantallen (lagen, monsters,
    // peilbuizen) zit in het uitklapbare detail.
    if (a.entity === 'import') {
      const aantal = a.count > 1 ? `${a.count} boringen` : a.count === 1 ? '1 boring' : '';
      return {
        lead: `importeerde het project uit een TerraIndex-bestand${aantal ? ` (${aantal})` : ''}`,
        subject: null,
        tail: '',
      };
    }

    // Bulk bijwerken uit een Excel/csv: één regel voor het hele bestand, op projectniveau.
    if (a.entity === 'boring' && a.action === 'bulk-update') {
      const aantal = a.count === 1 ? '1 boring' : `${a.count} boringen`;
      return { lead: `werkte ${aantal} bij uit een bestand`, subject: null, tail: '' };
    }

    if (sub) {
      const noun = { peilbuis: 'peilbuis', monster: 'grondmonster', layer: 'laag', watermonster: 'watermonster' }[a.entity];
      if (noun) {
        const verb = { create: a.entity === 'peilbuis' ? 'plaatste' : 'voegde', update: 'wijzigde', delete: 'verwijderde' }[a.action];
        if (verb === 'voegde') return { lead: `voegde ${noun}`, subject: sub, tail: 'toe aan' };
        if (a.action === 'update' && a.count > 1) return { lead: `wijzigde ${a.count} velden van ${noun}`, subject: sub, tail: 'van' };
        return { lead: `${verb} ${noun}`, subject: sub, tail: a.action === 'create' ? 'in' : 'van' };
      }
    }
    return { lead: this._phrase(a), subject: null, tail: '' };
  }

  /** Zin voor gebeurtenissen zonder benoemd onderdeel (o.a. oudere logregels). */
  _phrase(a) {
    // Meerdere veldwijzigingen aan dezelfde boring → één samenvattende zin.
    if ((a.entity === 'layer' || a.entity === 'boring') && a.action === 'update' && a.count > 1) {
      return `wijzigde ${a.count} velden van`;
    }
    const n = a.count > 1 ? `${a.count} ` : '';
    const map = {
      boring:      { create: 'maakte boring', update: 'wijzigde', delete: 'verwijderde boring' },
      layer:       { create: `voegde ${n}lagen toe aan`, update: 'wijzigde een laag van', delete: 'verwijderde een laag van' },
      monster:     { create: `nam ${n}grondmonster${a.count > 1 ? 's' : ''} bij`, update: 'wijzigde een grondmonster van', delete: 'verwijderde een grondmonster van' },
      peilbuis:    { create: 'plaatste een peilbuis in', update: 'wijzigde de peilbuis van', delete: 'verwijderde de peilbuis van',
                     process: 'digitaliseerde de peilbuis van' },
      watermonster:{ create: 'nam een watermonster bij', update: 'wijzigde een watermonster van', process: 'digitaliseerde het watermonster van' },
      afwerking:   { update: 'werkte de afwerking bij van', process: 'digitaliseerde de afwerking van' },
      photo:       { create: `voegde ${a.count > 1 ? `${a.count} foto's` : 'een foto'} toe aan boring`, update: 'wijzigde een foto in boring', delete: 'verwijderde een foto van boring' },
      recording:   { upload: 'sprak een opname in voor', process: 'liet de opname verwerken voor' },
      lagen:       { process: `digitaliseerde ${n}boring${a.count > 1 ? 'en' : ''} uit een opname` },
      // Zonder afsluitend 'van': een labopdracht hoort bij het project, niet bij een boring.
      labopdracht: { create: 'maakte een labopdracht aan', update: 'wijzigde een labopdracht',
                     delete: 'verwijderde een labopdracht',
                     send: 'verstuurde een labopdracht naar het lab' },
      // Idem voor een import. Deze terugval vangt oudere regels op; nieuwe komen langs het
      // eigen geval hierboven, dat het aantal boringen meeneemt.
      import: { create: 'importeerde het project uit een TerraIndex-bestand' },
    };
    const hit = map[a.entity]?.[a.action];
    if (hit) return hit;

    // Terugval in het Nederlands. Hiervoor stond hier `${a.action} ${a.entity}`, en dan lekte
    // "process peilbuis" naar de gebruiker zodra er een combinatie langskwam die nog niet in
    // de tabel stond -- ontwikkelaarstaal in een scherm dat een boormeester leest.
    const verb = { create: 'voegde', update: 'wijzigde', delete: 'verwijderde',
                   process: 'digitaliseerde', upload: 'uploadde' }[a.action] || 'wijzigde';
    const noun = { project: 'het project', boring: 'de boring', layer: 'een laag',
                   monster: 'een grondmonster', peilbuis: 'de peilbuis', watermonster: 'het watermonster',
                   afwerking: 'de afwerking', photo: 'een foto', recording: 'de opname' }[a.entity] || 'gegevens';
    return `${verb} ${noun} van`;
  }

  render() {
    const groups = this._grouped();
    return html`
      <section class="tile">
        <header>
          <!-- In de persoonspopup staat de naam al in de venstertitel; een tweede kop
               'Laatste activiteit' eronder zegt dan niets nieuws. -->
          <h3>${this.memberId ? 'Activiteit' : 'Laatste activiteit'}</h3>
          <!-- Het aantal dat je ziet, niet het aantal dat is opgehaald. Anders belooft de
               tegel er 26 en staan er 8. -->
          ${groups.length ? html`<span class="badge">${Math.min(groups.length, ROWS_ON_TILE)}</span>` : ''}
          <!-- Het filter staat bij 'toon alle activiteit', want daar heb je het nodig: in een
               lijst van duizend regels wil je op één soort kunnen kijken. -->
          <select class="kind" .value=${this._kind} @change=${(e) => { this._kind = e.target.value; }}
            title="Filter op soort gebeurtenis">
            ${KIND_FILTERS.map((k) => html`<option value=${k.value}>${k.label}</option>`)}
          </select>
          ${groups.length ? html`
            <button class="show-all" @click=${() => this._openAll()}>toon alle activiteit</button>` : ''}
        </header>

        ${this._loading && !this._items.length
          ? html`<p class="muted">Laden…</p>`
          : !groups.length
            ? html`<p class="muted">${this._kind ? 'Geen activiteit van deze soort.' : this.memberId ? 'Nog geen activiteit door deze gebruiker.' : 'Nog geen activiteit in dit project.'}</p>`
            : html`${this._byDay(groups.slice(0, ROWS_ON_TILE)).map((d) => this._renderDay(d))}`}

        ${this._allOpen ? this._renderAllModal(groups) : ''}

        ${this._lastRecording ? html`
          <div class="foot">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12" y2="20"/>
            </svg>
            Laatste opname uit de veldapp: ${formatDateTimeSlash(this._lastRecording)}
          </div>` : ''}
      </section>`;
  }


  /**
   * Eén activiteitsregel. Uitgelicht uit render() zodat de tegel en de volledige-geschiedenis-
   * popup exact dezelfde regel tonen; twee kopieën zouden onvermijdelijk uiteen gaan lopen.
   *
   * `key` maakt het uitklappen per weergave uniek: dezelfde gebeurtenis mag in de tegel dicht
   * staan terwijl hij in de popup open staat.
   */
  /**
   * Eén dag: een kop met de datum, daaronder de regels van die dag.
   *
   * `prefix` houdt het uitklappen van de tegel en dat van de popup uit elkaar — dezelfde
   * gebeurtenis mag in de tegel dicht staan terwijl hij in de popup open staat.
   */
  _renderDay(day, prefix = '') {
    return html`
      <div class="day">${day.label}</div>
      <ul>${day.rows.map((a) => this._renderRow(a, `${prefix}${a._key}`))}</ul>`;
  }

  _renderRow(a, key) {
    const canOpen = a.fields.length > 0;
    const open = !!this._open[key];
    const scope = this._scope(a);
    const parts = this._phraseParts(a, scope);
    const summary = this._summary(a, scope);
    const icon = ENTITY_ICON[a.entity] || Pencil;
    return html`
      <li class="${open ? 'is-open' : ''}">
        <div class="row ${canOpen ? 'clickable' : ''}"
          @click=${canOpen ? () => this._toggle(key) : null}>
          <span class="when">${this._time(a.createdAt)}</span>
          <span class="ico">${unsafeSVG(lucideIcon(icon, 14))}</span>
          <span class="what">
            <!-- Vet is voor de PERSOON, de accentkleur voor het object waar het over gaat, de
                 rest gewoon lopende tekst. Hiervoor waren de persoon, het onderdeel én de
                 boringnaam alle drie vet en wist je niet meer waar je naar keek. -->
            <b>${a.actorName || 'Onbekend'}</b> ${parts.lead}
            ${parts.subject ? html`<a class="sub" @click=${(e) => this._openBoring(e, a, this._tabFor(a, scope))} href="#">${parts.subject}</a>` : ''}
            ${parts.tail ? html` ${parts.tail}` : ''}
            ${a.boringName ? html` <a @click=${(e) => this._openBoring(e, a)} href="#">${a.boringName}</a>` : ''}
            <!-- Per persoon lopen de regels over projecten heen; zonder projectnaam is
                 'wijzigde boring B03' dan niet te plaatsen. -->
            ${this.memberId && (a.projectCode || a.projectName) ? html`
              <a class="proj" href=${`/projects/${a.projectId}`} @click=${(e) => e.stopPropagation()}>${a.projectCode || a.projectName}</a>` : ''}
            ${summary ? html`<span class="sum">${summary}</span>` : ''}
          </span>
          ${canOpen ? html`
            <button class="chev ${open ? 'up' : ''}" title=${open ? 'Inklappen' : 'Toon wat er is gewijzigd'}
              @click=${(e) => { e.stopPropagation(); this._toggle(key); }} aria-expanded=${open ? 'true' : 'false'}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>` : html`<span class="chev-spacer"></span>`}
        </div>
        ${open ? html`
          <div class="detail">
            <!-- Bij versturen is er niets 'gewijzigd': er is iets de deur uit gegaan, en dan
                 hoort er één kolom te staan met wat dat was. -->
            <span class="detail-head">${a.action === 'send' ? 'Wat er is verstuurd' : 'Gewijzigde velden'}</span>
            <table class="changes">
              ${a.fields.map((f) => html`
                <tr>
                  <td class="f">${this._fieldLabel(f.field, scope)}</td>
                  ${a.action === 'send' ? html`
                    <td class="to" colspan="3">${this._val(f.to, f.field)}</td>
                  ` : html`
                    <td class="from">${this._val(f.from, f.field)}</td>
                    <td class="arr">→</td>
                    <td class="to">${this._val(f.to, f.field)}</td>`}
                </tr>`)}
            </table>
          </div>` : ''}
      </li>`;
  }

  /**
   * Popup openen en pas dán de volledige geschiedenis ophalen.
   *
   * De tegel zelf heeft aan een handvol regels genoeg; honderden regels ophalen bij elke
   * paginaweergave is verspilling voor iets wat de meeste mensen nooit opendoen.
   */
  async _openAll() {
    this._allOpen = true;
    if (this._loadedAll) return;
    try {
      const res = await api.getProjectActivity(this.projectId, FETCH_ALL, this._kind);
      this._items = res?.activity || this._items;
      this._loadedAll = true;
    } catch (_) { /* dan tonen we wat we al hadden */ }
  }

  _toggle(key) { this._open = { ...this._open, [key]: !this._open[key] }; }

  /**
   * Volledige geschiedenis in een venster dat zelf schuift.
   *
   * De tegel toont bewust maar acht regels -- anders duwt hij de rest van de overzichtspagina
   * weg. Wie verder terug wil kijken doet dat hier, zonder de pagina te verlaten.
   */
  _renderAllModal(groups) {
    return html`
      <div class="modal-back" @click=${() => { this._allOpen = false; }}>
        <div class="modal" @click=${(e) => e.stopPropagation()}>
          <div class="modal-head">
            <h3>Alle activiteit</h3>
            <button class="modal-x" @click=${() => { this._allOpen = false; }} title="Sluiten">&times;</button>
          </div>
          <div class="modal-body">
            ${this._byDay(groups).map((d) => this._renderDay(d, 'm'))}
          </div>
        </div>
      </div>`;
  }

  _openBoring(e, a, tab = null) {
    e.preventDefault();
    e.stopPropagation();   // niet ook de rij uitklappen
    // Per persoon staat de tegel los van een projectpagina: er is geen ouder die het event
    // opvangt. Dan navigeren we naar het project, en dat opent de boring zelf.
    if (this.memberId) {
      if (a.projectId) window.location.href = `/projects/${a.projectId}`;
      return;
    }
    if (a.boringId) this.dispatchEvent(new CustomEvent('open-boring', { detail: { boringId: a.boringId, tab }, bubbles: true, composed: true }));
  }

  static styles = css`
    :host { display: block; }
    .tile { border: 1px solid var(--mv-border, #eff2f5); border-radius: .75rem; background: var(--mv-surface, #fff);
      padding: 1rem 1.15rem 1.1rem; box-shadow: var(--mv-shadow, 0 8px 24px rgba(82,63,105,.06)); }
    header { display: flex; align-items: center; gap: .5rem; margin-bottom: .85rem; }
    h3 { margin: 0; font-size: .95rem; color: var(--mv-text, #181c32); }
    .badge { background: var(--mv-surface-alt, #f3f4f6); color: var(--mv-text-sub, #6c757d);
      font-size: .7rem; font-weight: 700; border-radius: 99px; padding: 1px 7px; }
    .muted { color: var(--mv-text-sub, #6c757d); font-size: .85rem; margin: 0; }

    ul { list-style: none; margin: 0; padding: 0; }
    li { border-top: 1px solid var(--mv-border, #eff2f5); }
    li:first-child { border-top: none; }
    li.is-open { background: var(--mv-surface-alt, #fafbfc); border-radius: .5rem; }

    /* Dagkop. Klein en gedempt: hij ordent de lijst, hij is er niet zelf om gelezen te worden. */
    .day { font-size: .68rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase;
      color: var(--mv-text-sub, #9aa0a6); padding: .85rem .35rem .3rem; }
    .day:first-child { padding-top: .1rem; }

    .row.clickable { cursor: pointer; border-radius: .4rem; }
    .row.clickable:hover { background: var(--mv-surface-alt, #f5f6f8); }
    .row { display: flex; gap: .6rem; align-items: baseline; padding: .5rem .35rem; font-size: .85rem; }
    /* Alleen de tijd, dus smal — de datum staat in de dagkop. Tabular-nums houdt de cijfers
       onder elkaar uitgelijnd, anders danst de kolom bij elke regel. */
    .when { flex: 0 0 3rem; color: var(--mv-text-sub, #9aa0a6); font-size: .76rem;
      font-variant-numeric: tabular-nums; }
    .ico { flex: 0 0 auto; color: var(--mv-text-sub, #9aa0a6); display: inline-flex;
      /* Het icoon hoort optisch bij de eerste tekstregel, niet bij de basislijn ervan. */
      align-self: flex-start; margin-top: .15rem; }
    .what { flex: 1; min-width: 0; color: var(--mv-text, #181c32); line-height: 1.45; }
    /* Vet is voor de persoon en verder voor niets. */
    .what b { font-weight: 700; }
    /* De grijze tweede regel: één regel, afgekapt. Met CSS en niet met JS, zodat er precies
       zoveel van te zien is als er ruimte is -- een vast aantal tekens kapt op een breed
       scherm nodeloos af en loopt op een smal scherm alsnog om. */
    .sum { display: block; color: var(--mv-text-sub, #6c757d); font-size: .78rem; margin-top: .1rem;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    /* Projectchip per regel in de persoonsweergave: klein en grijs, zodat de zin leesbaar blijft. */
    .proj { display: inline-block; margin-left: .35rem; padding: 0 .45rem; border-radius: 999px; font-size: .7rem;
      font-weight: 700; color: var(--mv-text-sub, #6c757d); background: var(--mv-surface-alt, #f8f9fa);
      border: 1px solid var(--mv-border, #eff2f5); text-decoration: none; vertical-align: 1px; }

    /* Filter en 'toon alle activiteit' rechts; de teller blijft bij de titel. */
    /* Grijs vlak, zoals de andere velden: zonder achtergrond leest 'Alle soorten' als een
       label in plaats van als iets dat je kunt omzetten. */
    .kind { margin-left: auto; background: var(--mv-surface-alt, #f5f6f8); border: 1px solid var(--mv-border, #e4e6ef);
      cursor: pointer; font: inherit; font-size: .78rem; color: var(--mv-text, #181c32);
      padding: 4px 8px; border-radius: .4rem; }
    .kind:hover { background: var(--mv-bg, #eef0f3); }
    .show-all { background: none; border: none; padding: 0; cursor: pointer;
      font: inherit; font-size: .8rem; font-weight: 600; color: var(--mv-primary, #4b9d35); }
    .show-all:hover { text-decoration: underline; }

    /* Volledige geschiedenis. Vast maximum aan hoogte met een eigen schuifbalk, zodat een
       project met honderden regels het venster niet uit groeit. */
    .modal-back { position: fixed; inset: 0; z-index: 1000; background: rgba(15,23,42,.45);
      display: flex; align-items: center; justify-content: center; padding: 5vh 1rem; }
    .modal { background: var(--mv-surface, #fff); border-radius: .8rem; width: min(760px, 100%);
      max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(0,0,0,.25); }
    .modal-head { display: flex; align-items: center; gap: 10px; padding: 1rem 1.25rem .75rem;
      border-bottom: 1px solid var(--mv-border, #eff2f5); }
    .modal-head h3 { margin: 0; font-size: 1rem; font-weight: 700; color: var(--mv-text, #181c32); }
    .modal-x { margin-left: auto; background: none; border: none; cursor: pointer; font-size: 1.4rem;
      line-height: 1; color: var(--mv-text-sub, #6c757d); padding: 0 2px; }
    .modal-body { overflow-y: auto; padding: .25rem 1.25rem 1rem; }

    /* De accentkleur is voorbehouden aan het OBJECT waar de regel over gaat: de boring, de
       peilbuis, het watermonster. Niet vet — dat is van de persoon. Zo dragen kleur en
       gewicht elk één betekenis in plaats van allebei drie. */
    a { color: var(--mv-primary, #4b9d35); text-decoration: none; font-weight: 600; }
    a:hover { text-decoration: underline; }
    /* Het onderdeel (peilbuis/watermonster/laag) is óók klikbaar, maar mag de boringnaam
       niet overstemmen — die is het anker van de regel. */
    a.sub { color: var(--mv-text-sub, #6c757d); font-weight: 500;
      border-bottom: 1px dashed var(--mv-border-strong, #cfd8dc); }
    a.sub:hover { color: var(--mv-primary, #4b9d35); text-decoration: none; border-bottom-color: currentColor; }

    .chev { flex: 0 0 auto; background: none; border: none; cursor: pointer; padding: 2px; border-radius: .3rem;
      color: var(--mv-text-sub, #9aa0a6); display: inline-flex; align-items: center; transition: transform .15s ease; }
    .chev:hover { background: var(--mv-border, #eff2f5); color: var(--mv-text, #181c32); }
    .chev.up { transform: rotate(180deg); }
    .chev-spacer { flex: 0 0 17px; }

    /* Inspringen tot waar de tekst begint: tijdkolom + icoon + tussenruimtes. */
    .detail { padding: 0 .35rem .6rem 4.9rem; }
    .detail-head { display: block; font-size: .68rem; font-weight: 800; letter-spacing: .05em;
      text-transform: uppercase; color: var(--mv-text-sub, #9aa0a6); margin-bottom: .35rem; }
    .changes { border-collapse: collapse; font-size: .76rem; }
    .changes td { padding: 2px 0; vertical-align: baseline; }
    .changes .f { color: var(--mv-text-sub, #6c757d); padding-right: .9rem; white-space: nowrap; }
    .changes .from { color: var(--mv-text-sub, #9aa0a6); text-decoration: line-through; }
    .changes .arr { color: var(--mv-text-sub, #9aa0a6); padding: 0 .45rem; }
    .changes .to { color: var(--mv-text, #181c32); font-weight: 600; }

    .foot { display: flex; align-items: center; gap: .4rem; margin-top: .8rem; padding-top: .6rem;
      border-top: 1px solid var(--mv-border, #eff2f5); color: var(--mv-text-sub, #6c757d); font-size: .75rem; }
  `;
}

customElements.define('activity-tile', ActivityTile);
